import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Editor } from 'app/components/editor/Editor';
import Navbar from 'app/components/editor/navbar/Navbar';
import Sidebar from 'app/components/editor/sidebar/Sidebar';
import { SubToolbar } from 'app/components/editor/subtoolbar/SubToolbar';
import { SubNavBar } from 'app/components/editor/navbar/SubNavbar';
import withMentions from 'app/slate/extensions/mentions';
import withImportArea from 'app/slate/extensions/import-area';
import withNotes from 'app/slate/extensions/notes';
import withPage from 'app/slate/extensions/page';
import withParagraph from 'app/slate/extensions/paragraph';
import withLayout from 'app/slate/extensions/layout';
import withImage from 'app/slate/extensions/image';
import withReferenceTable from 'app/slate/extensions/reference-table';
import withTableOfContent from 'app/slate/extensions/table-of-content';
import useLock from './useLock';
import ReadOnlyMark from 'app/components/editor/ReadOnlyMark';
import { usePermissions } from 'app/utils/hooks/document-permission';
import withBookmarks from 'app/slate/extensions/bookmark';
import withLinks from 'app/slate/extensions/link';
import { useBookmarks } from 'app/utils/hooks/useBookmarks';
import withStyledParagraph from 'app/slate/extensions/styled-paragraph';
import withHighlight from 'app/slate/extensions/highlight';
import withTag from 'app/slate/extensions/tag';
import { TagContextProvider } from 'app/state/contexts/TagContext';
import { SidebarContextProvider } from 'app/state/contexts/SidebarContext';
import { EditorModeContextProvider, useEditorMode } from 'app/state/contexts/EditorContext';
import { withLists } from 'app/slate/extensions/list';
import { useQuery } from 'app/slate/utils';
import { withDecorateComments } from 'app/slate/decoraters/commentDecorator';
import { TagsToolbar } from 'app/components/editor/subtoolbar/tags/TagsToolbar';
import { TagTextDeleteWarning } from 'app/components/modal/TagTextDeleteWarning';

export const DocumentEditor = () => {
  return (
    <EditorModeContextProvider>
      <TagContextProvider>
        <SidebarContextProvider>
          <DocumentEditorInner />
        </SidebarContextProvider>
      </TagContextProvider>
    </EditorModeContextProvider>
  );
};

const DocumentEditorInner = () => {
  const { documentId, sectionId } = useParams();
  const documentRevisionId = useQuery().get('revision');
  const sectionRevisionId = useQuery().get('sectionRevision');
  const translationId = useQuery().get('translation');
  const isDraft = useQuery().get('useDraft') ?? false;
  const instance = { documentId, documentRevisionId, sectionId, sectionRevisionId, translationId };
  const [showFormatting, setShowFormatting] = useState(false);
  const { hasLock, lockInfo, deleteLock } = useLock(documentId, sectionId);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const { sectionPermissions } = usePermissions(documentId);
  const { isTaggingMode } = useEditorMode();
  const showSubToolbar = Object.values(sectionPermissions).some((value) => value);
  const toolbarComponents = { styling: !isReadOnly, insert: !isReadOnly, languange: true };
  const { deleteLink, deleteBookmark, createLink } = useBookmarks(documentId);
  useEffect(() => {
    setIsReadOnly(!hasLock || !!isDraft || showFormatting || isTaggingMode);
  }, [hasLock, isDraft, showFormatting, isTaggingMode]);
  const extensions = [
    withLayout({ markEmpty: true }),
    withPage({ withButtons: true, readOnly: isReadOnly }),
    withMentions,
    withImportArea({ readOnly: isReadOnly, withComments: true, withTags: true }),
    withImage({ readOnly: isReadOnly }),
    withPage({ withButtons: true, readOnly: isReadOnly }),
    withNotes,
    withParagraph({ withComments: true, withStyledParagrah: true, withTags: true }),
    withReferenceTable({ readOnly: isReadOnly }),
    withTableOfContent({ readOnly: isReadOnly }),
    withBookmarks({ deleteBookmark, createLink }),
    withLinks({ deleteLink }),
    withStyledParagraph({ isPDF: false }),
    withHighlight({}),
    withTag({ isPDF: false }),
    withLists({}),
  ];
  const decorators = [withDecorateComments({ sectionSuperId: sectionId })];
  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      if (hasLock) {
        await deleteLock();
      }
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('popstate', handleBeforeUnload);
    return async () => {
      window.removeEventListener('popstate', handleBeforeUnload);
      if (hasLock) {
        await deleteLock();
      }
    };
  }, [hasLock, deleteLock]);

  return (
    <div className={`document-editor ${showSubToolbar ? 'sub-navbar-padding' : ''}`}>
      <Editor sectionHeader autoSave showFormatting={showFormatting} instance={instance} extensions={extensions} readOnly={isReadOnly} decorators={decorators}>
        <Navbar readOnly={isReadOnly} isDraft={isDraft} />
        {!isTaggingMode && <ReadOnlyMark readOnly={isReadOnly} lockOwner={lockInfo.lockOwner} />}
        <SubNavBar visibile={showSubToolbar}>
          <ToolbarSwitcher toolbarComponents={toolbarComponents} showFormatting={showFormatting} setShowFormatting={setShowFormatting} />
        </SubNavBar>
        <Sidebar readOnly={isReadOnly} />
        <TagTextDeleteWarning />
      </Editor>
    </div>
  );
};

const ToolbarSwitcher = ({ toolbarComponents, showFormatting, setShowFormatting }) => {
  const { mode } = useEditorMode();

  if (mode === 'tagging') {
    return <TagsToolbar />;
  }
  return <SubToolbar enabled={toolbarComponents} showFormatting={showFormatting} setShowFormatting={setShowFormatting} />;
};
